// Mirrors the gateway's RunStatuses.TERMINAL. FLOW DELETED is the enum's display value, so both spellings count.
const FINALIZED_STATUSES = new Set([
  "FAILED",
  "COMPLETED",
  "WARNING",
  "ABORTED",
  "STOPPED",
  "TERMINATED",
  "FLOW_DELETED",
  "FLOW DELETED",
]);

/** A finished run no longer changes what it logged; anything not known to be finished is treated as live. */
export function isFinalized(status?: string | null): boolean {
  return !!status && FINALIZED_STATUSES.has(status);
}

interface ReadEntry {
  detailState?: string;
  readWhileLive?: boolean;
}

/** Text read while the run was live is stale once the run has finished: it is read once more, then kept. */
export function isStaleSinceFinish(
  entry: ReadEntry | null | undefined,
  finalized: boolean
): boolean {
  return (
    finalized &&
    !!entry &&
    entry.readWhileLive === true &&
    entry.detailState === "loaded"
  );
}

/** Reads of a finished run are kept per key, so each is asked for once; a live run always reads through. */
export function createFinalizedReads<T>(): (
  key: string,
  finalized: boolean,
  load: () => Promise<T>
) => Promise<T> {
  const kept = new Map<string, T>();
  return async (key, finalized, load) => {
    if (finalized && kept.has(key)) {
      return kept.get(key) as T;
    }
    const result = await load();
    // A failed or empty answer is not kept, so the next attempt asks again.
    const usable = Array.isArray(result) ? result.length > 0 : !!result;
    if (finalized && usable) {
      kept.set(key, result);
    }
    return result;
  };
}
